import { Map, Record, List } from "immutable";
import { LocationRecord, locationFromJSON } from "common/records/Location";
import { RootActions, RootState } from "common/reducers";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { reducer as idsManagementReducer } from "common/reducers/idsManagement.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import {
  ACTIVATE_LOCATION,
  DESTROY_LOCATION,
  FETCH_LOCATION,
  LocationActions,
  RECEIVE_LOCATION,
  RECEIVE_LOCATIONS,
  REMOVE_LOCATION,
  RESET_LOCATION,
  SET_LOCATION_LOADED,
} from "common/reducers/location.actions";

export const LocationStateRecord = Record({
  byId: Map<string | number, LocationRecord>(),
  lastSavedById: Map<string | number, LocationRecord>(),
});

export const initialState = LocationStateRecord();

export type LocationState = typeof initialState;

export const model = "locations";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case FETCH_LOCATION:
      return fetch(state, model, locationFromJSON({ id: action.payload.locationId })) as RootState;

    case RECEIVE_LOCATIONS:
      const locations = List(action.payload.locations);
      locations.forEach((location) => {
        state = reducer(state, LocationActions.receiveLocation(location));
      });

      const activeIds = locations
        .filter((location) => Boolean(location.org_location_link))
        .sortBy((location) => location.org_location_link?.sort_order)
        .map((location) => location.id);
      const availableIds = locations.filter((location) => !location.org_location_link).map((location) => location.id);

      state = idsManagementReducer(state, IdsManagementActions.receiveModel(model + ".active", activeIds));
      state = idsManagementReducer(state, IdsManagementActions.receiveModel(model + ".available", availableIds));

      return state;

    case RECEIVE_LOCATION:
      const locationRecord = locationFromJSON(action.payload.location);
      state = receive(state, model, locationRecord) as RootState;

      if (action.payload.pushToList) {
        state = idsManagementReducer(state, IdsManagementActions.pushId(locationRecord.id, model + ".active"));
      }
      return state;

    case RESET_LOCATION:
      return reset(state, model, action.payload.locationId) as RootState;

    case SET_LOCATION_LOADED:
      return loaded(state, model, action.payload.locationId) as RootState;

    case DESTROY_LOCATION:
      state = idsManagementReducer(
        state,
        IdsManagementActions.removeId(action.payload.locationId, model + ".available"),
      );

      return state
        .deleteIn([model, "byId", action.payload.locationId])
        .deleteIn([model, "lastSavedById", action.payload.locationId]);

    case REMOVE_LOCATION: {
      state = idsManagementReducer(state, IdsManagementActions.pushId(action.payload.locationId, model + ".available"));
      state = idsManagementReducer(state, IdsManagementActions.removeId(action.payload.locationId, model + ".active"));

      return state.updateIn([model, "byId", action.payload.locationId], (location: LocationRecord) =>
        location.set("org_location_link", null),
      );
    }
    case ACTIVATE_LOCATION: {
      state = idsManagementReducer(state, IdsManagementActions.pushId(action.payload.locationId, model + ".active"));
      state = idsManagementReducer(
        state,
        IdsManagementActions.removeId(action.payload.locationId, model + ".available"),
      );

      return state;
    }

    default:
      return state;
  }
};
